import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Upload, Info, X, AlertTriangle } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { RankSelect } from "../ui/rank-select";
import { useSnapshot } from "valtio";
import { authStore } from "../../stores/authStore";

import { profileApi } from "../../api/profileApi";
import { handleFileUpload } from "../../api/filesApi";

// Must match the server's image rule (file.service.ts: JPEG/PNG/WebP, 10MB).
const PROFILE_PIC_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PROFILE_PIC_MAX_BYTES = 10 * 1024 * 1024;
const SID_MAX_BYTES = 5 * 1024 * 1024;

// A saved image comes back as a storage key, not a loadable URL.
const toImageUrl = (value) => {
  if (typeof value !== "string" || !value) return null;
  if (/^(https?:|blob:|data:)/.test(value)) return value;
  return `${import.meta.env.VITE_CDN_LINK}${value}`;
};

const REQUIRED_FIELD_LABELS = {
  name: "Name",
  age: "Age",
  gender: "Gender",
  mobileNumber: "Mobile Number",
  aadhaarNumber: "Aadhaar Number",
  rankDesignation: "Rank/Designation",
  currentShipName: "Ship Name",
  currentShipImo: "IMO Number",
  profilePic: "Profile Picture",
  sidFile: "SID PDF",
};

export default function ProfileCreationForm({ profile, closeForm, onUpdate }) {
  const snap = useSnapshot(authStore);
  const userData = profile || snap.user || null;
  // Get immutable fields from auth state
  const immutableEmail = userData?.email || "";
  const immutableCdc = userData?.indianCdc || "";

  const [formData, setFormData] = useState({
    name: userData?.name || "",
    age: userData?.age || "",
    gender: userData?.gender || "Male",
    mobileNumber: userData?.mobileNumber || "",
    email: immutableEmail,
    aadhaarNumber: userData?.aadhaarNumber || "",
    indianCdc: immutableCdc,
    rankDesignation: userData?.rankDesignation || "",
    currentShipName: userData?.currentShipName || "",
    currentShipImo: userData?.currentShipImo || "",
    address: userData?.address || "",
  });

  const [profilePic, setProfilePic] = useState(userData?.profileImage || null);

  const [sidFile, setSidFile] = useState(userData?.seafarerSid || null);
  const [profilePicPreview, setProfilePicPreview] = useState(
    toImageUrl(userData?.profileImage)
  );

  const [showImgModal, setShowImgModal] = useState(false);
  const [profilePicError, setProfilePicError] = useState("");
  const [sidFileError, setSidFileError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: null,
    age: null,
    gender: null,
    email: null,
    mobileNumber: null,
    aadhaarNumber: null,
    indianCdc: null,
    rankDesignation: null,
    currentShipName: null,
    currentShipImo: null,
    profilePic: null,
    sidFile: null,
    address: null,
  });

  // When the form opens, pre-fill with available user data
  useEffect(() => {
    const immutableEmail = userData?.email || "";
    const immutableCdc = userData?.indianCdc || "";

    setFormData((prev) => ({
      ...prev,
      name: userData?.name || "",
      age: userData?.age || "",
      gender: userData?.gender || "Male",
      email: immutableEmail,
      mobileNumber: userData?.mobileNumber || "",
      aadhaarNumber: userData?.aadhaarNumber || "",
      indianCdc: immutableCdc,
      rankDesignation: userData?.rankDesignation || "",
      currentShipName: userData?.currentShipName || "",
      currentShipImo: userData?.currentShipImo || "",
    }));
    setProfilePic(userData?.profileImage || null);
    setProfilePicPreview(toImageUrl(userData?.profileImage));
    const sid = userData?.seafarerSid || null;
    if (typeof sid === "string") {
      // Already-saved SID: shown as a link, never re-uploaded.
      setSidFile({ name: sid.split("/").pop(), url: toImageUrl(sid) });
    } else {
      setSidFile(sid);
    }
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      const onlyNums = value.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: onlyNums,
      }));
    } else if (name === "email" || name === "indianCdc") {
      // Prevent changing email and CDC number
      return;
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleProfilePicChange = async (e) => {
    setProfilePicError("");
    setErrors((prev) => ({ ...prev, profilePic: null }));
    const file = e.target.files && e.target.files[0];
    // Reset so picking the same file again (after fixing it) fires onChange.
    e.target.value = "";
    if (!file) return;
    const rejectWith = (message) => {
      setProfilePicError(message);
      setErrors((prev) => ({ ...prev, profilePic: message }));
    };
    if (!PROFILE_PIC_TYPES.includes(file.type)) {
      // iPhones save photos as HEIC by default; the server only takes these.
      rejectWith(
        /heic|heif/i.test(file.type || file.name)
          ? "iPhone HEIC photos aren't supported. Please upload a JPG or PNG."
          : "Please upload a JPG, PNG or WebP image."
      );
      return;
    }
    if (file.size > PROFILE_PIC_MAX_BYTES) {
      rejectWith("Image must be smaller than 10MB.");
      return;
    }
    setProfilePic(file);
    setProfilePicPreview(URL.createObjectURL(file));
  };

  const handleSidFileChange = (e) => {
    setSidFileError("");
    setErrors((prev) => ({ ...prev, sidFile: null }));
    const file = e.target.files && e.target.files[0];
    // Reset so picking the same file again (after fixing it) fires onChange.
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      setSidFile(null);
      setSidFileError("Only PDF files are allowed.");
      setErrors((prev) => ({
        ...prev,
        sidFile: "Only PDF files are allowed.",
      }));
      return;
    }
    if (file.size > SID_MAX_BYTES) {
      setSidFile(null);
      setSidFileError("File size must be less than 5MB.");
      setErrors((prev) => ({
        ...prev,
        sidFile: "File size must be less than 5MB.",
      }));
      return;
    }
    setSidFile(file);
  };

  const handleSubmit = async () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.age) newErrors.age = "Age is required.";
    if (!formData.gender.trim()) newErrors.gender = "Gender is required.";
    if (!formData.aadhaarNumber.trim())
      newErrors.aadhaarNumber = "Aadhaar Number is required.";
    if (!formData.rankDesignation.trim())
      newErrors.rankDesignation = "Rank/Designation is required.";
    if (!formData.currentShipName.trim())
      newErrors.currentShipName = "Ship Name is required.";
    if (!formData.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile Number is required.";
    if (!formData.currentShipImo.trim())
      newErrors.currentShipImo = "IMO Number is required.";
    if (!profilePic) newErrors.profilePic = "Profile picture is required.";
    if (!sidFile) newErrors.sidFile = "SID PDF is required.";

    // These used to be computed and then ignored, so a half-filled form was
    // submitted anyway (or Submit stayed disabled with no explanation).
    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      toast.error(
        "Please complete: " +
          Object.keys(newErrors)
            .map((key) => REQUIRED_FIELD_LABELS[key] || key)
            .join(", ")
      );
      return;
    }

    setSubmitLoading(true);
    try {
      // Upload only files picked in this session; a saved photo/SID is a
      // string or {name, url} and stays as it is. handleFileUpload reports
      // failure in its result instead of throwing, so check it: before, a
      // failed upload saved the profile without the file and still said
      // "Profile updated successfully!".
      const uploadIfNew = async (file, label) => {
        if (!(file instanceof File)) return null;
        const result = await handleFileUpload(file);
        if (!result?.success || !result.fileUploadId) {
          const reason =
            result?.error?.message ||
            (typeof result?.error === "string" ? result.error : null) ||
            "please try again";
          throw new Error(`${label} upload failed: ${reason}`);
        }
        return result;
      };
      const profilePicUpload = await uploadIfNew(profilePic, "Profile picture");
      const sidFileUpload = await uploadIfNew(sidFile, "SID");

      const updatedProfilePic = profilePicUpload?.fileUrl || null;
      const updatedProfilePicId = profilePicUpload?.fileUploadId || null;
      const updatedSidFileUrl = sidFileUpload?.fileUploadId || null;
      const profileData = {
        name: formData.name,
        age: parseInt(formData.age, 10),
        gender: formData.gender,
        aadhaarNumber: formData.aadhaarNumber,
        rankDesignation: formData.rankDesignation,
        currentShipName: formData.currentShipName,
        currentShipImo: formData.currentShipImo,
        mobileNumber: formData.mobileNumber,
      };
      if (updatedProfilePicId) {
        profileData.profileImageId = updatedProfilePicId;
      }
      if (updatedSidFileUrl) {
        profileData.seafarerSidId = updatedSidFileUrl;
      }
      await profileApi.updateProfile(profileData);
      if (updatedProfilePic) setProfilePicPreview(updatedProfilePic);
      toast.success("Profile updated successfully!");
      closeForm();
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to update profile. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="w-full h-full min-h-0 flex flex-col relative">
      <div className="flex-shrink-0 px-4 pt-4 sm:px-6 sm:pt-6">
        <div className="flex items-center justify-between w-full mb-2">
          <h1 className="text-2xl sm:text-3xl font-normal text-[var(--secondary-font)]">
            Profile Creation
          </h1>
          {userData &&
            Object.keys(userData).length > 0 &&
            userData.name !== "" && (
              <button
                className="p-2 text-white hover:text-[var(--primary-font)] bg-[var(--primary-font)] hover:bg-white rounded-lg border !border-[var(--primary-font)] transition-all"
                onClick={() => closeForm()}
                aria-label="Close"
                disabled={userData?.name === null}
                style={{
                  opacity: userData?.name === null ? 0.5 : 1,
                  pointerEvents: userData?.name === null ? "none" : "auto",
                }}
              >
                <X className="w-5 h-5" />
              </button>
            )}
        </div>

        <div className="flex items-center gap-3 mb-4 text-[var(--secondary-font)]">
          <Info className="w-5 h-5 shrink-0 text-[var(--secondary-font)]" />
          <span className="text-sm sm:text-base">
            Please complete your profile to access the E-learning content.
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6">
      <div className="space-y-6 sm:space-y-8 pb-4">
        {/* Name, Age, Gender Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.name ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Name
            </label>
            <div className="relative space-y-1">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter name..."
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 ${errors.name ? "!border-red-600" : "!border-slate-300"}`}
              />
              {errors.name && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.name}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.age ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Your age
            </label>
            <div className="relative space-y-1">
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                placeholder="Age..."
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 ${errors.age ? "!border-red-600" : "!border-slate-300"}`}
              />
              {errors.age && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.age}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.gender ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Select Gender
            </label>
            <div className="relative space-y-1">
              <div className="flex gap-2">
                {[
                  { key: "Male", value: "MALE" },
                  { key: "Female", value: "FEMALE" },
                  { key: "Others", value: "OTHERS" },
                ].map((gender) => (
                  <button
                    key={gender.key}
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        gender: gender.value,
                      }));
                      setErrors((prev) => ({ ...prev, gender: undefined }));
                    }}
                    className={`px-6 py-3 rounded-xl text-base font-medium transition-all ${
                      formData.gender === gender.value
                        ? "bg-[var(--primary-font)] text-white shadow-sm"
                        : "bg-slate-50 text-slate-500 hover:bg-[var(--primary-font)] hover:text-white"
                    }`}
                  >
                    {gender.key}
                  </button>
                ))}
              </div>
              {errors.gender && (
                <span className="text-xs text-red-600 block mt-2">
                  {errors.gender}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Profile Picture and Email Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.profilePic ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Profile Picture
            </label>
            <div
              className={`relative w-full flex flex-1 items-center gap-2 px-6 py-4 bg-white text-slate-600 rounded-xl border cursor-pointer ${errors.profilePic ? "!border-red-600" : "!border-slate-300"}`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
                id="profilePicInput"
                onChange={handleProfilePicChange}
              />
              {profilePicPreview ? (
                <div
                  className="flex items-center justify-center gap-2 cursor-pointer"
                  onClick={() => setShowImgModal(true)}
                  title="Click to view full image"
                >
                  <img
                    src={profilePicPreview}
                    alt="Profile Preview"
                    className="w-8 h-6 object-cover rounded-sm cursor-pointer"
                    title="Click to preview"
                  />
                  <span className="text-xs text-slate-400">
                    Click to view full image
                  </span>
                </div>
              ) : (
                <span
                  onClick={() =>
                    document.getElementById("profilePicInput").click()
                  }
                  className="cursor-pointer select-none opacity-60"
                >
                  Upload
                </span>
              )}
              <span
                className="absolute top-1/2 -translate-y-1/2 right-4 p-3 text-slate-400 hover:text-slate-600 border !border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer"
                onClick={() =>
                  document.getElementById("profilePicInput").click()
                }
                title="Upload image"
              >
                <Upload className="w-5 h-5" />
              </span>
            </div>
            {profilePicError || errors.profilePic ? (
              <span className="text-xs text-red-600 ml-4 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {profilePicError || errors.profilePic}
              </span>
            ) : (
              <span className="text-xs text-slate-400 flex items-center ml-4">
                {"JPG, PNG or WebP, up to 10MB."}
              </span>
            )}
            {showImgModal && profilePicPreview && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                <button
                  type="button"
                  aria-label="Close preview"
                  className="absolute top-8 right-8 z-50 bg-[var(--secondary-font)] hover:bg-[var(--primary-font)] text-white rounded-lg p-2 shadow-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  onClick={() => setShowImgModal(false)}
                >
                  <X className="w-6 h-6" />
                </button>
                <img
                  src={profilePicPreview}
                  alt="Preview"
                  className="max-w-full max-h-full bg-white rounded-xl shadow-xl"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.email ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Email
            </label>
            <div className="relative space-y-1">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                readOnly
                placeholder="Email"
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 !border-slate-400 opacity-50`}
                disabled
              />
              {errors.email && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.email}
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.mobileNumber ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Mobile Number
            </label>
            <div className="relative space-y-1">
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="Enter Mobile Number..."
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 ${errors.mobileNumber ? "!border-red-600" : "!border-slate-300"}`}
              />
              {errors.mobileNumber && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.mobileNumber}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* aadhaar, CDC, Rank Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.aadhaarNumber ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Aadhaar number
            </label>
            <div className="relative space-y-1">
              <input
                type="text"
                name="aadhaarNumber"
                value={formData.aadhaarNumber.replace(
                  /(\d{1,4})(\d{1,4})?(\d{1,4})?/,
                  (m, g1, g2, g3) => [g1, g2, g3].filter(Boolean).join(" ")
                )}
                onChange={(e) => {
                  // Only allow digits, max 12
                  setErrors((prev) => ({ ...prev, aadhaarNumber: null }));
                  let raw = e.target.value.replace(/\D/g, "").slice(0, 12);
                  setFormData((prev) => ({ ...prev, aadhaarNumber: raw }));
                }}
                placeholder="XXXX XXXX XXXX"
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 ${errors.aadhaarNumber ? "!border-red-600" : "!border-slate-300"}`}
              />
              {errors.aadhaarNumber && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.aadhaarNumber}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.indianCdc ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              INDoS Number
            </label>
            <div className="relative space-y-1">
              <input
                type="text"
                id="indianCdc"
                name="indianCdc"
                value={formData.indianCdc}
                readOnly
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 !border-slate-400 opacity-50`}
                placeholder="INDoS Number"
                disabled
              />
              {errors.indianCdc && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.indianCdc}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.rankDesignation ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Rank/Designation
            </label>
            <div className="relative space-y-1">
              <RankSelect
                value={formData.rankDesignation}
                onValueChange={(value) => {
                  setFormData((prev) => ({ ...prev, rankDesignation: value }));
                  setErrors((prev) => ({
                    ...prev,
                    rankDesignation: undefined,
                  }));
                }}
                placeholder="Search and select rank…"
                invalid={!!errors.rankDesignation}
                triggerClassName={`w-full px-4 py-4 border rounded-xl text-base ${errors.rankDesignation ? "!border-red-600" : "!border-slate-300"}`}
                allowOther
              />
              {errors.rankDesignation && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.rankDesignation}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* SID, Ship Name, IMO Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.sidFile ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              SID (Seafarer's Identity Document)
            </label>
            <div
              className={`relative w-full flex flex-1 items-center gap-2 px-6 py-4 bg-white text-slate-600 rounded-xl border cursor-pointer ${errors.sidFile ? "!border-red-600" : "!border-slate-300"}`}
            >
              <input
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                id="sidFileInput"
                onChange={handleSidFileChange}
                required
              />
              {sidFile?.url ? (
                <a
                  href={sidFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate underline text-blue-500"
                  title="View uploaded PDF"
                >
                  {sidFile.name || "View file"}
                </a>
              ) : sidFile ? (
                // Picked but not uploaded yet: nothing to link to.
                <span className="truncate text-slate-700">{sidFile.name}</span>
              ) : (
                <span
                  onClick={() =>
                    document.getElementById("sidFileInput").click()
                  }
                  className="cursor-pointer select-none opacity-60"
                >
                  Upload
                </span>
              )}
              <span
                className="absolute top-1/2 -translate-y-1/2 right-4 p-3 text-slate-400 hover:text-slate-600 border !border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer"
                onClick={() => document.getElementById("sidFileInput").click()}
                title="Upload PDF"
              >
                <Upload className="w-5 h-5" />
              </span>
            </div>
            {sidFileError || errors.sidFile ? (
              <span className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {sidFileError || errors.sidFile}
              </span>
            ) : (
              <span className="text-xs text-slate-400 flex items-center ml-4">
                {"File size must be less than 5MB."}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.currentShipName ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              Last/Current Ship's Name
            </label>
            <div className="relative space-y-1">
              <input
                type="text"
                name="currentShipName"
                value={formData.currentShipName}
                onChange={handleInputChange}
                placeholder="Enter name..."
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 ${errors.currentShipName ? "!border-red-600" : "!border-slate-300"}`}
              />
              {errors.currentShipName && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.currentShipName}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label
              className={`block text-base font-medium ${errors.currentShipImo ? "text-red-600" : "text-[var(--secondary-font)]"}`}
            >
              IMO Number
            </label>
            <div className="relative space-y-1">
              <input
                type="text"
                name="currentShipImo"
                value={formData.currentShipImo}
                onChange={handleInputChange}
                placeholder="Enter number..."
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 focus:!border-transparent text-base placeholder-slate-400 ${errors.currentShipImo ? "!border-red-600" : "!border-slate-300"}`}
              />
              {errors.currentShipImo && (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {errors.currentShipImo}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Submit stays visible on short screens */}
      <div className="flex-shrink-0 w-full flex justify-end items-center gap-4 px-4 py-3 sm:px-6 sm:py-4 border-t border-slate-200 bg-white">
          <button
            type="button"
            onClick={handleSubmit}
            // Always clickable unless saving: a click on an incomplete form
            // now lists what's missing instead of the button silently refusing.
            className={`w-full sm:w-auto px-8 sm:px-16 md:px-24 py-3.5 sm:py-4 bg-[var(--primary-font)] text-white rounded-xl transition-colors font-medium text-base ${
              !submitLoading ? "hover:bg-[var(--secondary-font)]" : ""
            }`}
            disabled={submitLoading}
            style={{
              opacity: submitLoading ? 0.5 : 1,
              cursor: submitLoading ? "not-allowed" : "pointer",
            }}
          >
            {submitLoading ? "Saving..." : "Submit"}
          </button>
      </div>
    </div>
  );
}
